import React, { useState, useEffect, useRef } from 'react'
import { db } from '../../helpers/firebase'
import { collection, query, where, getDocs } from 'firebase/firestore'
import CelebrationOverlay from '../UiElements/CelebrationOverlay'
import './ParentChecklist.css'

const CHECKLIST_ITEMS = [
  {
    id: 'create-student',
    emoji: '👩‍🎓',
    title: 'Create a student account',
    description: 'Add your first student so they can start practicing.',
    required: true
  },
  {
    id: 'complete-question',
    emoji: '✅',
    title: 'Complete a question',
    description: "Have your student answer their first question in the Study Helper.",
    required: true
  },
  {
    id: 'add-page',
    emoji: '📄',
    title: 'Add a page',
    description: 'Upload a textbook page or worksheet to build a question bank.',
    required: false
  },
  {
    // Phase 3 (Onboarding Demo). Optional by product decision: parents who
    // only Add Student should never be blocked by this. demoTutorialCompleted
    // comes from the parent's own profile (passed in as demoCompleted) rather
    // than a Firestore read here, since it is not scoped to any student.
    id: 'complete-demo',
    emoji: '🎬',
    title: 'Completed Demo',
    description: 'Try the interactive Explain & Check Answer demo with your child.',
    required: false
  }
]

const ParentChecklist = ({ currentUser, students, demoCompleted = false, autoOpen = false, onAddStudent, onOpenDemo, onOpenChange }) => {
  const celebratedKey = currentUser ? `pcl_celebrated_${currentUser.uid}` : null
  const [isOpen, setIsOpen] = useState(autoOpen)
  const [checks, setChecks] = useState({
    'create-student': false,
    'complete-question': false,
    'add-page': false,
    'complete-demo': demoCompleted
  })
  const [showConfetti, setShowConfetti] = useState(false)
  const [showXpPop, setShowXpPop] = useState(false)
  const [celebrated, setCelebrated] = useState(
    () => celebratedKey ? localStorage.getItem(celebratedKey) === '1' : false
  )
  const [loading, setLoading] = useState(true)
  const panelRef = useRef(null)
  const btnRef = useRef(null)
  const suppressCloseRef = useRef(autoOpen)

  useEffect(() => {
    if (autoOpen) {
      setIsOpen(true)
      suppressCloseRef.current = true
      const timer = setTimeout(() => { suppressCloseRef.current = false }, 1000)
      return () => clearTimeout(timer)
    }
  }, [autoOpen])

  useEffect(() => {
    if (currentUser) {
      checkProgress()
    }
  }, [currentUser, students, demoCompleted])

  // Lets ParentDashboard (and, through it, App-level chrome like What's New)
  // know when this panel — not just the always-present FAB — is actually
  // expanded on screen, so a full-screen surface (competition enrollment,
  // release notes) never renders underneath/behind it at the same moment the
  // panel auto-opens after the legacy tutorial finishes.
  useEffect(() => {
    if (onOpenChange) onOpenChange(isOpen)
    // Unmounting (e.g. ParentDashboard swaps this out for the demo/Add
    // Student modal) must also clear the flag — otherwise a panel that was
    // open at the moment it got torn down would wedge parentTaskModalActive
    // "on" for the rest of the session.
    return () => { if (onOpenChange) onOpenChange(false) }
  }, [isOpen, onOpenChange])

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        suppressCloseRef.current
      ) return
      if (
        isOpen &&
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [isOpen])

  useEffect(() => {
    const requiredDone = CHECKLIST_ITEMS
      .filter(i => i.required)
      .every(i => checks[i.id])

    if (requiredDone && !celebrated && !loading) {
      setCelebrated(true)
      if (celebratedKey) localStorage.setItem(celebratedKey, '1')
      setShowConfetti(true)
      setShowXpPop(true)
      setIsOpen(true)
      setTimeout(() => setShowConfetti(false), 5000)
      setTimeout(() => setShowXpPop(false), 2000)
    }
  }, [checks, celebrated, loading])

  const checkProgress = async () => {
    try {
      setLoading(true)
      const newChecks = {
        'create-student': false,
        'complete-question': false,
        'add-page': false,
        'complete-demo': demoCompleted
      }

      newChecks['create-student'] = Array.isArray(students) && students.length > 0

      if (Array.isArray(students) && students.length > 0) {
        let hasQuestion = false
        let hasPage = false

        for (const student of students) {
          if (!hasQuestion) {
            try {
              const questionsRef = collection(db, 'users', student.id, 'studyQuestions')
              const qSnap = await getDocs(query(questionsRef, where('status', '!=', 'unanswered')))
              if (qSnap.size === 0) {
                const allQSnap = await getDocs(questionsRef)
                const answered = allQSnap.docs.find(d => {
                  const data = d.data()
                  return data.feedback || data.answer || (data.attempts && data.attempts.length > 0) || (data.allAttempts && data.allAttempts.length > 0)
                })
                if (answered) hasQuestion = true
              } else {
                hasQuestion = true
              }
            } catch (err) {
              console.error('Error checking questions:', err)
            }
          }

          if (!hasPage) {
            try {
              const pagesRef = collection(db, 'users', student.id, 'pages')
              const pagesSnap = await getDocs(pagesRef)
              if (pagesSnap.size > 0) hasPage = true
            } catch (err) {
              console.error('Error checking pages:', err)
            }
          }

          if (hasQuestion && hasPage) break
        }

        newChecks['complete-question'] = hasQuestion
        newChecks['add-page'] = hasPage
      }

      setChecks(newChecks)
    } catch (err) {
      console.error('Error checking checklist progress:', err)
    } finally {
      setLoading(false)
    }
  }

  const completedRequired = CHECKLIST_ITEMS.filter(i => i.required).every(i => checks[i.id])
  const completedCount = Object.values(checks).filter(Boolean).length
  const totalCount = CHECKLIST_ITEMS.length

  const progressPct = Math.round((completedCount / totalCount) * 100)

  return (
    <>
      <CelebrationOverlay
        showConfetti={showConfetti}
        showXpPop={showXpPop}
        xpAmount={50}
      />

      {!completedRequired && (
        <button
          ref={btnRef}
          className={`pcl-fab ${isOpen ? 'pcl-fab--open' : ''}`}
          onClick={() => setIsOpen(v => !v)}
          aria-label="Getting Started Checklist"
          title="Getting Started Checklist"
        >
          <span className="pcl-fab-icon">📋</span>
          {completedCount > 0 && (
            <span className="pcl-fab-badge">{completedCount}/{totalCount}</span>
          )}
        </button>
      )}

      {isOpen && (
        <div ref={panelRef} className="pcl-panel">
          <div className="pcl-panel-header">
            <div className="pcl-panel-header-top">
              <div className="pcl-panel-title">
                {completedRequired ? '🎉 You\'re all set!' : 'Getting Started'}
              </div>
              <button className="pcl-panel-close" onClick={() => setIsOpen(false)}>×</button>
            </div>
            {completedRequired ? (
              <div className="pcl-panel-subtitle">
                Your student is ready to learn. You've completed the essentials!
              </div>
            ) : (
              <div className="pcl-panel-subtitle">
                Complete these steps to get the most out of Quillnote.
              </div>
            )}

            <div className="pcl-progress-bar-track">
              <div
                className={`pcl-progress-bar-fill ${completedRequired ? 'pcl-progress-bar-fill--done' : ''}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="pcl-progress-label">
              {completedCount} of {totalCount} completed
            </div>
          </div>

          <div className="pcl-items">
            {CHECKLIST_ITEMS.map(item => {
              const done = checks[item.id]
              const isClickable =
                (item.id === 'create-student' && !done && !!onAddStudent) ||
                (item.id === 'complete-demo' && !done && !!onOpenDemo)
              const handleClick = () => {
                setIsOpen(false)
                if (item.id === 'create-student') onAddStudent()
                else if (item.id === 'complete-demo') onOpenDemo()
              }
              return (
                <div
                  key={item.id}
                  className={`pcl-item ${done ? 'pcl-item--done' : ''} ${isClickable ? 'pcl-item--clickable' : ''}`}
                  onClick={isClickable ? handleClick : undefined}
                >
                  <div className={`pcl-item-check ${done ? 'pcl-item-check--done' : ''}`}>
                    {done ? '✓' : <span className="pcl-item-emoji">{item.emoji}</span>}
                  </div>
                  <div className="pcl-item-text">
                    <div className="pcl-item-title">
                      {item.title}
                      {!item.required && (
                        <span className="pcl-item-optional">optional</span>
                      )}
                    </div>
                    <div className="pcl-item-desc">{item.description}</div>
                  </div>
                </div>
              )
            })}
          </div>

          {loading && (
            <div className="pcl-loading">Checking your progress...</div>
          )}
        </div>
      )}
    </>
  )
}

export default ParentChecklist
